import React, { useState } from 'react';
import axios from 'axios';
import FakeEventWarning from './FakeEventWarning';
import { translateText, detectLanguage } from './translationUtils';
import './QuickEventForm.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickEventForm = ({ position, categories, onClose, onEventCreated, language, user }) => {
  const [formData, setFormData] = useState({
    category: '',
    description: '', // Single description field
    address: '',
    fullName: (user && user.full_name) || '',
    phone: (user && user.phone) || '',
    is_active: true
  });
  const [photos, setPhotos] = useState([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showWarning, setShowWarning] = useState(true);
  const [warningAccepted, setWarningAccepted] = useState(false);

  const t = {
    kk: {
      title: "Жылдам оқиға қосу",
      coordinates: "Координаттар",
      category: "Санат",
      selectCategory: "Санатты таңдаңыз",
      description: "Сипаттама",
      address: "Мекенжай (міндетті емес)",
      fullName: "Тегі Аты Әкесінің аты",
      phone: "Телефон нөмірі",
      photos: "Оқиға орнынан фото",
      selectPhotos: "Фото таңдаңыз",
      removePhoto: "Фотоны жою",
      active: "Белсенді",
      create: "Жасау",
      cancel: "Болдырмау",
      creating: "Жасалуда..."
    },
    ru: {
      title: "Быстрое добавление события",
      coordinates: "Координаты",
      category: "Категория",
      selectCategory: "Выберите категорию",
      description: "Описание",
      address: "Адрес (необязательно)",
      fullName: "Фамилия Имя Отчество",
      phone: "Номер телефона",
      photos: "Фото с места события",
      selectPhotos: "Выберите фото",
      removePhoto: "Удалить фото",
      active: "Активно",
      create: "Создать",
      cancel: "Отмена",
      creating: "Создание..."
    }
  };

  const translations = t[language] || t.ru;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    setPhotos(files);
    
    // Create preview URLs
    const urls = files.map(file => URL.createObjectURL(file));
    setPhotoPreviewUrls(urls);
    setError('');
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    const newUrls = photoPreviewUrls.filter((_, i) => i !== index);
    
    // Revoke old URL
    URL.revokeObjectURL(photoPreviewUrls[index]);
    
    setPhotos(newPhotos);
    setPhotoPreviewUrls(newUrls);
  };

  const handleWarningAccept = () => {
    setWarningAccepted(true);
    setShowWarning(false);
  };

  const handleWarningCancel = () => {
    setShowWarning(false);
    onClose(); // Close the entire form
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Block submission if warning not accepted
    if (!warningAccepted) {
      setShowWarning(true);
      return;
    }

    if (!formData.category) {
      setError(language === 'kk' ? 'Санатты таңдаңыз' : 'Выберите категорию');
      return;
    }
    
    if (!formData.description.trim()) {
      setError(language === 'kk' ? 'Сипаттаманы толтырыңыз' : 'Заполните описание');
      return;
    }

    if (!formData.fullName.trim()) {
      setError(language === 'kk' ? 'Тегі Аты Әкесінің атын толтырыңыз' : 'Заполните Фамилию Имя Отчество');
      return;
    }

    if (!formData.phone.trim()) {
      setError(language === 'kk' ? 'Телефон нөмірін толтырыңыз' : 'Заполните номер телефона');
      return;
    }

    if (photos.length === 0) {
      setError(language === 'kk' ? 'Оқиға орнынан кемінде бір фото қосыңыз' : 'Добавьте минимум одно фото с места события');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('category', formData.category);
      submitData.append('lat', position.lat.toString());
      submitData.append('lng', position.lng.toString());
      
      // Auto-translate description to both languages
      const description = formData.description.trim();
      const detectedLang = detectLanguage(description);
      
      if (detectedLang === 'kk') {
        submitData.append('description_kk', description);
        submitData.append('description_ru', translateText(description, 'kk', 'ru'));
      } else {
        submitData.append('description_ru', description);
        submitData.append('description_kk', translateText(description, 'ru', 'kk'));
      }
      if (formData.address.trim()) {
        submitData.append('address', formData.address.trim());
      }
      submitData.append('reporter_full_name', formData.fullName.trim());
      submitData.append('reporter_phone', formData.phone.trim());
      submitData.append('priority', '1');
      
      // If user is service, auto-assign event to them and set status to assigned
      if (user && user.role === 'service') {
        submitData.append('assigned_to', user.username || user.id);
        submitData.append('status', 'assigned');
      }
      
      // Add photos
      photos.forEach((photo, index) => {
        submitData.append('photos', photo);
      });

      const response = await axios.post(`${API}/events/with-photos`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data) {
        onEventCreated(response.data);
        onClose();
      }
    } catch (error) {
      console.error('Error creating event:', error);
      setError(language === 'kk' ? 'Оқиғаны жасау кезінде қате орын алды' : 'Ошибка при создании события');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quick-event-overlay">
      <div className="quick-event-form">
        <div className="quick-event-header">
          <h3>{translations.title}</h3>
          <button 
            className="close-btn"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="coordinates-display">
            <label>{translations.coordinates}:</label>
            <span>{position.lat.toFixed(6)}, {position.lng.toFixed(6)}</span>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>{translations.category} *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
              disabled={loading}
            >
              <option value="">{translations.selectCategory}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat[`name_${language}`] || cat.name_ru}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{translations.description} *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={language === 'kk' ? 'Оқиғаның сипаттамасын жазыңыз' : 'Опишите событие'}
              required
              disabled={loading}
              rows="4"
            />
            <div className="description-help">
              {language === 'kk' 
                ? 'Қазақ немесе орыс тілінде жаза аласыз - автоматты аударма жасалады'
                : 'Можете писать на казахском или русском языке - автоматический перевод'
              }
            </div>
          </div>

          <div className="form-group">
            <label>{translations.fullName} *</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Иванов Иван Иванович"
              required
              disabled={loading}
              data-testid="reporter-fullname-input"
            />
          </div>

          <div className="form-group">
            <label>{translations.phone} *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+7 700 123 45 67"
              required
              disabled={loading}
              data-testid="reporter-phone-input"
            />
          </div>

          <div className="form-group">
            <label>{translations.address}</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder={language === 'kk' ? 'Абай көшесі, 123' : 'ул. Примерная, 123'}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>{translations.photos} *</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoChange}
              disabled={loading}
              className="photo-input"
              required
              data-testid="event-photo-input"
            />
            <div className="photo-help">
              {translations.selectPhotos} (до 5 фото, максимум 5MB каждое)
            </div>
          </div>

          {photoPreviewUrls.length > 0 && (
            <div className="form-group">
              <div className="photo-previews">
                {photoPreviewUrls.map((url, index) => (
                  <div key={`photo-preview-${index}-${url.slice(-10)}`} className="photo-preview">
                    <img src={url} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-photo-btn"
                      onClick={() => removePhoto(index)}
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                disabled={loading}
              />
              {translations.active}
            </label>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              {translations.cancel}
            </button>
            <button
              type="submit"
              className="create-btn"
              disabled={loading}
            >
              {loading ? translations.creating : translations.create}
            </button>
          </div>
        </form>
      </div>

      {/* Warning modal */}
      {showWarning && !warningAccepted && (
        <FakeEventWarning
          onAccept={handleWarningAccept}
          onCancel={handleWarningCancel}
          language={language}
        />
      )}
    </div>
  );
};

export default QuickEventForm;